// src/components/superadmin/SuperAdminReports.jsx
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { superAdminReportService } from '../../services/superAdminReportService';

import {
  Box, Container, Typography, Paper, Button, Grid,
  Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, LinearProgress, Chip, Divider,
  Menu, MenuItem, Fade, Tooltip, Tabs, Tab, Stack,
  Skeleton, TextField, InputAdornment, IconButton,
  TablePagination,
} from '@mui/material';

import FileDownloadIcon        from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon        from '@mui/icons-material/PictureAsPdf';
import KeyboardArrowDownIcon   from '@mui/icons-material/KeyboardArrowDown';
import Inventory2Icon          from '@mui/icons-material/Inventory2';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import PersonAddAlt1Icon       from '@mui/icons-material/PersonAddAlt1';
import MemoryIcon              from '@mui/icons-material/Memory';
import TrendingDownIcon        from '@mui/icons-material/TrendingDown';
import CurrencyRupeeIcon       from '@mui/icons-material/CurrencyRupee';
import PeopleIcon              from '@mui/icons-material/People';
import AdminPanelSettingsIcon  from '@mui/icons-material/AdminPanelSettings';
import BusinessIcon            from '@mui/icons-material/Business';
import LocationOnIcon          from '@mui/icons-material/LocationOn';
import CategoryIcon            from '@mui/icons-material/Category';
import PieChartIcon            from '@mui/icons-material/PieChart';
import InsightsIcon            from '@mui/icons-material/Insights';
import SearchIcon              from '@mui/icons-material/Search';
import ClearIcon               from '@mui/icons-material/Clear';
import WarningAmberIcon        from '@mui/icons-material/WarningAmber';
import SpeedIcon               from '@mui/icons-material/Speed';
import EmojiEventsIcon         from '@mui/icons-material/EmojiEvents';
import SortIcon                from '@mui/icons-material/Sort';
import FilterListIcon          from '@mui/icons-material/FilterList';
import LeaderboardIcon         from '@mui/icons-material/Leaderboard';

// ── PDF Libraries ─────────────────────────────────────────────────────────────
let jsPDF;
let autoTable;
const loadPDFLibraries = async () => {
  try {
    const { default: m } = await import('jspdf');
    jsPDF = m;
    const at = await import('jspdf-autotable');
    autoTable = at.default;
  } catch (e) {
    console.warn('PDF export not available:', e.message);
  }
};
loadPDFLibraries();

// ── Skeleton ──────────────────────────────────────────────────────────────────
const ReportSkeleton = () => (
  <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
      <Box>
        <Skeleton variant="text" width={320} height={44} />
        <Skeleton variant="text" width={420} height={20} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Skeleton variant="rounded" width={120} height={40} />
      </Box>
    </Box>
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
      {Array.from({ length: 8 }, (_, i) => (
        <Box key={i} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e0e0e0', background: '#fff' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
            <Skeleton variant="text" width={80} height={14} />
            <Skeleton variant="rounded" width={32} height={32} />
          </Box>
          <Skeleton variant="text" width={120} height={44} />
          <Skeleton variant="text" width={160} height={14} sx={{ mt: 1 }} />
        </Box>
      ))}
    </Box>
    <Skeleton variant="rounded" width="100%" height={300} sx={{ borderRadius: 3 }} />
    <Skeleton variant="rounded" width="100%" height={400} sx={{ borderRadius: 3 }} />
  </Container>
);

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatCurrency = (val) =>
  typeof val === 'number' ? `₹${val.toLocaleString('en-IN')}` : '₹0';

const formatDate = (ts) => {
  if (!ts) return 'N/A';
  if (ts instanceof Date) return ts.toLocaleDateString();
  if (ts?.toDate) return ts.toDate().toLocaleDateString();
  if (ts?.seconds) return new Date(ts.seconds * 1000).toLocaleDateString();
  if (typeof ts === 'string') return new Date(ts).toLocaleDateString();
  return 'N/A';
};

const getPerformanceColor = (score) => {
  if (score >= 80) return 'success';
  if (score >= 60) return 'info';
  if (score >= 40) return 'warning';
  return 'error';
};

const getPerformanceClass = (score) => {
  if (score >= 80) return 'excellent';
  if (score >= 60) return 'good';
  if (score >= 40) return 'average';
  return 'poor';
};

const performanceBadgeSx = (score) => {
  const cls = getPerformanceClass(score);
  const map = {
    excellent: { bgcolor: '#e8f5e9', color: '#2e7d32' },
    good:      { bgcolor: '#e8f5e9', color: '#2e7d32' },
    average:   { bgcolor: '#fff3e0', color: '#ef6c00' },
    poor:      { bgcolor: '#ffebee', color: '#c62828' },
  };
  return {
    ...map[cls],
    px: 1.25, py: 0.5, borderRadius: '12px',
    fontSize: '0.75rem', fontWeight: 700, display: 'inline-block',
  };
};

const matchesSearch = (company, term) =>
  ['name', 'contactPerson', 'email', 'location'].some((key) =>
    company[key]?.toLowerCase().includes(term),
  );

// ── Main Component ────────────────────────────────────────────────────────────

const SuperAdminReports = () => {
  const { currentUser } = useAuth();

  const [reportData, setReportData] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [sortBy, setSortBy] = useState('totalAssets');
  const [sortOrder, setSortOrder] = useState('desc');

  // Table
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Tabs
  const [distTab, setDistTab] = useState(0);

  // Export menu
  const [exportAnchorEl, setExportAnchorEl] = useState(null);

  // ── Fetch via service layer ──
  useEffect(() => {
    if (!currentUser) {
      setError('User not authenticated');
      setLoading(false);
      return;
    }

    const fetchReport = async () => {
      try {
        setLoading(true);
        const { analytics: a, reports } = await superAdminReportService.buildReport();
        setAnalytics(a);
        setReportData(reports);
      } catch (err) {
        console.error('[SuperAdminReports] Error:', err);
        setError(`Failed to load report: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [currentUser]);

  // ── Filter & sort ──
  const filteredData = useMemo(() => {
    let filtered = reportData;
    filtered = filtered.filter((c) => c.name !== 'N/A');

    // Search
    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter((c) => matchesSearch(c, lower));
    }

    // Date range
    if (dateRange !== 'all') {
      const filterDate = new Date();
      if (dateRange === '30days') filterDate.setDate(filterDate.getDate() - 30);
      else if (dateRange === '90days') filterDate.setDate(filterDate.getDate() - 90);
      else if (dateRange === '1year') filterDate.setFullYear(filterDate.getFullYear() - 1);

      filtered = filtered.filter((c) => {
        const d = superAdminReportService.toDate(c.createdAt);
        return d && d >= filterDate;
      });
    }

    // Sort
    filtered = [...filtered].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      return sortOrder === 'asc'
        ? aVal > bVal ? 1 : -1
        : aVal < bVal ? 1 : -1;
    });

    return filtered;
  }, [reportData, searchTerm, dateRange, sortBy, sortOrder]);

  // ── Totals ──
  const totals = useMemo(() => ({
    companies: filteredData.length,
    assets: filteredData.reduce((s, c) => s + c.totalAssets, 0),
    unassigned: filteredData.reduce((s, c) => s + c.unassignedAssets, 0),
    assigned: filteredData.reduce((s, c) => s + c.assignedAssets, 0),
    utilized: filteredData.reduce((s, c) => s + c.utilizedAssets, 0),
    value: filteredData.reduce((s, c) => s + c.totalAssetValue, 0),
    depreciated: filteredData.reduce((s, c) => s + c.depreciatedValue, 0),
    users: filteredData.reduce((s, c) => s + c.totalUsers, 0),
    maintenanceIssues: filteredData.reduce((s, c) => s + c.maintenanceIssues, 0),
    warrantyIssues: filteredData.reduce((s, c) => s + c.warrantyIssues, 0),
    avgPerformance: filteredData.length > 0
      ? filteredData.reduce((s, c) => s + c.performanceScore, 0) / filteredData.length
      : 0,
  }), [filteredData]);

  // ── CSV Export ──
  const exportToCSV = useCallback(() => {
    const header =
      'Company,Contact,Email,Phone,Location,Total Assets,Unassigned,Assigned,Utilized,Performance,Maintenance Issues,Warranty Issues,Depreciated Value,Total Value,Users,Admins,Asset Types,Avg Age (months),Created';
    const rows = filteredData.map((c) =>
      [
        c.name, c.contactPerson, c.email, c.phone, c.location,
        c.totalAssets, c.unassignedAssets, c.assignedAssets, c.utilizedAssets,
        c.performanceScore.toFixed(1), c.maintenanceIssues, c.warrantyIssues,
        c.depreciatedValue.toFixed(0), c.totalAssetValue.toFixed(0),
        c.totalUsers, c.adminUsers, c.assetTypes, c.averageAssetAge.toFixed(1),
        formatDate(c.createdAt),
      ].join(','),
    );

    rows.push('', 'SUMMARY');
    rows.push(`Total Companies,${totals.companies}`);
    rows.push(`Total Assets,${totals.assets}`);
    rows.push(`Total Investment,${totals.value.toFixed(0)}`);
    rows.push(`Current Value,${totals.depreciated.toFixed(0)}`);

    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `superadmin_report_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredData, totals]);

  // ── PDF Export ──
  const exportToPDF = useCallback(async () => {
    if (!jsPDF || !autoTable) {
      await loadPDFLibraries();
      if (!jsPDF || !autoTable) {
        alert('PDF export not available.');
        return;
      }
    }
    try {
      const doc = new jsPDF('landscape');
      doc.setFontSize(22);
      doc.text('SuperAdmin Platform Report', 148, 25, { align: 'center' });
      doc.setFontSize(11);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 148, 35, { align: 'center' });

      autoTable(doc, {
        startY: 50,
        head: [['Metric', 'Value', 'Metric', 'Value']],
        body: [
          ['Companies', totals.companies, 'Avg Performance', `${totals.avgPerformance.toFixed(1)}/100`],
          ['Total Assets', totals.assets, 'Utilization', `${totals.assets > 0 ? ((totals.utilized / totals.assets) * 100).toFixed(1) : 0}%`],
          ['Investment', formatCurrency(totals.value), 'Maintenance Issues', totals.maintenanceIssues],
          ['Current Value', formatCurrency(totals.depreciated), 'Warranty Issues', totals.warrantyIssues],
          ['Users', totals.users, 'Engagement', `${(analytics.userEngagementRate || 0).toFixed(1)}%`],
        ],
        theme: 'grid',
        headStyles: { fillColor: [52, 58, 64] },
      });

      doc.addPage();
      doc.setFontSize(16);
      doc.text('Company Performance', 15, 20);
      autoTable(doc, {
        startY: 30,
        head: [['Company', 'Assets', 'Score', 'Utilization', 'Value', 'Users', 'Issues']],
        body: filteredData.slice(0, 25).map((c) => [
          c.name.length > 18 ? `${c.name.substring(0, 18)}...` : c.name,
          c.totalAssets,
          `${c.performanceScore.toFixed(0)}/100`,
          `${c.totalAssets > 0 ? ((c.utilizedAssets / c.totalAssets) * 100).toFixed(0) : 0}%`,
          formatCurrency(c.totalAssetValue),
          c.totalUsers,
          c.maintenanceIssues + c.warrantyIssues,
        ]),
        theme: 'grid',
        headStyles: { fillColor: [39, 174, 96] },
      });

      const totalPages = doc.internal.pages.length - 1;
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(9);
        doc.setTextColor(150);
        doc.text(
          `Page ${i} of ${totalPages} | SuperAdmin Platform Report`,
          148,
          doc.internal.pageSize.height - 10,
          { align: 'center' },
        );
      }
      doc.save(`superadmin_report_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('PDF generation failed. Try CSV export.');
    }
  }, [filteredData, totals, analytics]);

  // ── Guards ──
  if (loading) return <ReportSkeleton />;
  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2.5 }}>{error}</Alert>
      </Container>
    );
  }

  // ── KPI Cards config ──
  const kpiCards = [
    {
      label: 'Total Companies', value: totals.companies,
      subtitle: analytics.newCompaniesThisMonth > 0
        ? `+${analytics.newCompaniesThisMonth} this month`
        : 'No new companies this month',
      icon: <BusinessIcon />, color: 'primary.main', bg: 'rgba(21,101,192,0.08)',
    },
    {
      label: 'Platform Assets', value: totals.assets,
      subtitle: `${((totals.utilized / Math.max(totals.assets, 1)) * 100).toFixed(1)}% utilized`,
      icon: <Inventory2Icon />, color: 'success.main', bg: 'rgba(46,125,50,0.08)',
    },
    {
      label: 'Total Investment', value: formatCurrency(totals.value),
      subtitle: `${(analytics.depreciationRate || 0).toFixed(1)}% depreciated`,
      icon: <CurrencyRupeeIcon />, color: 'warning.main', bg: 'rgba(237,108,2,0.08)',
    },
    {
      label: 'Current Value', value: formatCurrency(totals.depreciated),
      subtitle: 'After depreciation',
      icon: <TrendingDownIcon />, color: 'info.main', bg: 'rgba(2,136,209,0.08)',
    },
    {
      label: 'Platform Users', value: totals.users,
      subtitle: `${(analytics.userEngagementRate || 0).toFixed(1)}% active`,
      icon: <PeopleIcon />, color: 'secondary.main', bg: 'rgba(156,39,176,0.08)',
    },
    {
      label: 'Avg Performance', value: `${totals.avgPerformance.toFixed(1)}/100`,
      subtitle: 'Across all companies',
      icon: <SpeedIcon />, color: 'primary.main', bg: 'rgba(21,101,192,0.08)',
    },
    {
      label: 'Unassigned Assets', value: totals.unassigned,
      subtitle: `${((totals.unassigned / Math.max(totals.assets, 1)) * 100).toFixed(1)}% of total`,
      icon: <RemoveCircleOutlineIcon />, color: 'warning.dark', bg: 'rgba(237,108,2,0.06)',
    },
  ];

  // ── Distribution tabs ──
  const distTabs = [
    {
      label: 'Asset Types', icon: <CategoryIcon fontSize="small" />,
      data: (analytics.assetTypeDistribution || []).map((i) => ({ label: i.type, count: i.count })),
      color: 'primary', colorHex: 'rgba(21,101,192,0.08)', header: 'Asset Type', total: totals.assets,
    },
    {
      label: 'Company Size', icon: <BusinessIcon fontSize="small" />,
      data: (analytics.companySizeDistribution || []).map((i) => ({ label: i.size, count: i.count })),
      color: 'secondary', colorHex: 'rgba(156,39,176,0.08)', header: 'Size Category', total: totals.companies,
    },
    {
      label: 'Company Locations', icon: <LocationOnIcon fontSize="small" />,
      data: (analytics.locationDistribution || []).map((i) => ({ label: i.location, count: i.count })),
      color: 'info', colorHex: 'rgba(2,136,209,0.08)', header: 'Location', total: totals.companies,
    },
  ];

  const activeDistTab = distTabs[distTab];

  // ── Top performers ──
  const topPerformers = [...filteredData]
    .filter(c => c.name !== 'N/A')
    .sort((a, b) => b.performanceScore - a.performanceScore)
    .slice(0, 5);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>

      {/* ── HEADER ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, letterSpacing: '-0.5px' }}>
            SuperAdmin Platform Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comprehensive report across all companies, assets, and users
          </Typography>
        </Box>
        <Button
          variant="outlined" startIcon={<FileDownloadIcon />} endIcon={<KeyboardArrowDownIcon />}
          onClick={(e) => setExportAnchorEl(e.currentTarget)}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          Export
        </Button>
        <Menu
          anchorEl={exportAnchorEl} open={Boolean(exportAnchorEl)}
          onClose={() => setExportAnchorEl(null)} TransitionComponent={Fade}
          PaperProps={{ sx: { minWidth: 180, borderRadius: 2, mt: 0.5 } }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => { setExportAnchorEl(null); exportToCSV(); }} sx={{ gap: 1.5 }}>
            <FileDownloadIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={500}>Export CSV</Typography>
          </MenuItem>
          <MenuItem onClick={() => { setExportAnchorEl(null); exportToPDF(); }} sx={{ gap: 1.5 }}>
            <PictureAsPdfIcon fontSize="small" color="error" />
            <Typography variant="body2" fontWeight={500}>Export PDF</Typography>
          </MenuItem>
        </Menu>
      </Box>

      {/* ── ALERT BAR ── */}
      {(totals.maintenanceIssues + totals.warrantyIssues) > 0 && (
        <Alert severity="warning" sx={{ borderRadius: 2.5 }} icon={<WarningAmberIcon />}>
          <strong>Platform Alert:</strong> {totals.maintenanceIssues + totals.warrantyIssues} total issues across all companies require attention
        </Alert>
      )}

      {/* ── KPI CARDS ── */}
      <Grid container spacing={2}>
        {kpiCards.map((card, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Paper variant="outlined" sx={{
              borderRadius: 3, p: 2.5, height: '100%',
              transition: 'transform 0.15s, box-shadow 0.15s',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' },
            }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.68rem' }}>
                  {card.label}
                </Typography>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: card.bg, color: card.color, display: 'flex' }}>
                  {card.icon}
                </Box>
              </Stack>
              <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.5px', lineHeight: 1.1 }} noWrap>
                {card.value}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontWeight: 500 }}>
                {card.subtitle}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* ── TOP PERFORMERS ── */}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <EmojiEventsIcon color="warning" fontSize="small" />
          <Typography variant="h6" fontWeight={700}>Top Performing Companies</Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Rank', 'Company', 'Assets', 'Users', 'Performance', 'Utilization', 'Value'].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700, bgcolor: 'background.neutral' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {topPerformers.map((c, i) => (
              <TableRow key={c.id} hover>
                <TableCell>
                  <Chip label={`#${i + 1}`} size="small" color={i === 0 ? 'warning' : 'default'}
                    sx={{ fontWeight: 700, fontSize: '0.72rem' }} />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{c.name}</TableCell>
                <TableCell>{c.totalAssets}</TableCell>
                <TableCell>{c.totalUsers}</TableCell>
                <TableCell>
                  <Chip label={`${c.performanceScore.toFixed(0)}`} size="small"
                    color={getPerformanceColor(c.performanceScore)}
                    sx={{ fontWeight: 700, fontSize: '0.72rem', minWidth: 44 }} />
                </TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <LinearProgress variant="determinate"
                      value={c.totalAssets > 0 ? (c.utilizedAssets / c.totalAssets) * 100 : 0}
                      color="success"
                      sx={{ height: 6, borderRadius: 99, width: 60, bgcolor: 'rgba(46,125,50,0.08)' }} />
                    <Typography variant="caption" fontWeight={600}>
                      {c.totalAssets > 0 ? ((c.utilizedAssets / c.totalAssets) * 100).toFixed(0) : 0}%
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{formatCurrency(c.totalAssetValue)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* ── DISTRIBUTION ── */}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PieChartIcon color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={700}>Platform Distribution</Typography>
          <Chip label={`${activeDistTab.data.length} items`} size="small" variant="outlined"
            sx={{ ml: 'auto', fontWeight: 600, fontSize: '0.7rem' }} />
        </Box>
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}>
          <Tabs value={distTab} onChange={(_, v) => setDistTab(v)}
            sx={{ minHeight: 44, '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' } }}>
            {distTabs.map((tab, idx) => (
              <Tab key={idx} iconPosition="start" icon={tab.icon}
                label={
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <span>{tab.label}</span>
                    <Chip label={tab.data.length} size="small" sx={{
                      height: 18, fontSize: '0.65rem', fontWeight: 700,
                      bgcolor: distTab === idx ? tab.colorHex : 'action.hover',
                    }} />
                  </Stack>
                }
              />
            ))}
          </Tabs>
        </Box>
        {activeDistTab.data.length > 0 ? (
          <TableContainer>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  {[activeDistTab.header, 'Distribution', 'Count', '%'].map((h, i) => (
                    <TableCell key={i} align={i >= 2 ? (i === 2 ? 'center' : 'right') : 'left'}
                      sx={{ fontWeight: 700, bgcolor: 'background.neutral', ...(i === 1 && { width: '50%' }) }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {activeDistTab.data.map((item, i) => {
                  const pct = activeDistTab.total > 0
                    ? (item.count / activeDistTab.total) * 100 : 0;
                  return (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{item.label}</TableCell>
                      <TableCell>
                        <Tooltip title={`${item.count} of ${activeDistTab.total}`} arrow>
                          <LinearProgress variant="determinate" value={pct}
                            color={activeDistTab.color}
                            sx={{ height: 8, borderRadius: 99, bgcolor: activeDistTab.colorHex,
                              '& .MuiLinearProgress-bar': { borderRadius: 99 } }} />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={item.count} size="small" color={activeDistTab.color} variant="outlined"
                          sx={{ fontWeight: 700, fontSize: '0.72rem', minWidth: 44 }} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        {pct.toFixed(1)}%
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 4 }}>
            <Alert severity="info" sx={{ borderRadius: 2.5 }}>No data for {activeDistTab.label.toLowerCase()}</Alert>
          </Box>
        )}
      </Paper>

      {/* ── SEARCH & FILTERS ── */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" placeholder="Search companies..."
              value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}><ClearIcon fontSize="small" /></IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField select fullWidth size="small" label="Time Range"
              value={dateRange} onChange={(e) => { setDateRange(e.target.value); setPage(0); }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
              <MenuItem value="all">All Time</MenuItem>
              <MenuItem value="30days">Last 30 Days</MenuItem>
              <MenuItem value="90days">Last 90 Days</MenuItem>
              <MenuItem value="1year">Last Year</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField select fullWidth size="small" label="Sort By"
              value={sortBy} onChange={(e) => setSortBy(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
              <MenuItem value="totalAssets">Total Assets</MenuItem>
              <MenuItem value="performanceScore">Performance</MenuItem>
              <MenuItem value="totalAssetValue">Asset Value</MenuItem>
              <MenuItem value="name">Company Name</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={1}>
            <Tooltip title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}>
              <IconButton onClick={() => setSortOrder((o) => o === 'asc' ? 'desc' : 'asc')}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <SortIcon sx={{ transform: sortOrder === 'asc' ? 'scaleY(-1)' : 'none' }} />
              </IconButton>
            </Tooltip>
          </Grid>
          {searchTerm && (
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                Found <strong>{filteredData.length}</strong> companies matching "{searchTerm}"
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* ── DETAILED TABLE ── */}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LeaderboardIcon color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={700}>
            Detailed Company Analysis ({filteredData.length})
          </Typography>
        </Box>

        {filteredData.length === 0 ? (
          <Box sx={{ p: 6 }}>
            <Alert severity="info" sx={{ borderRadius: 2.5 }}>No companies match your filters.</Alert>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small" sx={{ minWidth: 1200 }}>
                <TableHead>
                  <TableRow>
                    {['Company', 'Contact', 'Location', 'Score', 'Assets', 'Utilization',
                      'Value', 'Users', 'Admins', 'Avg Age', 'Created'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700, bgcolor: 'background.neutral', whiteSpace: 'nowrap' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((c) => (
                      <TableRow key={c.id} hover>
                        <TableCell sx={{ fontWeight: 600, maxWidth: 180 }} title={c.name}>
                          <Typography variant="body2" fontWeight={600} noWrap>{c.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>{c.contactPerson}</Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <LocationOnIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                            <Typography variant="body2" noWrap>{c.location}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip label={c.performanceScore.toFixed(0)} size="small"
                            color={getPerformanceColor(c.performanceScore)}
                            sx={{ fontWeight: 700, fontSize: '0.72rem', minWidth: 40 }} />
                        </TableCell>
                        <TableCell>{c.totalAssets}</TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <LinearProgress variant="determinate"
                              value={c.totalAssets > 0 ? (c.utilizedAssets / c.totalAssets) * 100 : 0}
                              color="success"
                              sx={{ height: 6, borderRadius: 99, width: 60, bgcolor: 'rgba(46,125,50,0.08)' }} />
                            <Typography variant="caption" fontWeight={600}>
                              {c.totalAssets > 0 ? ((c.utilizedAssets / c.totalAssets) * 100).toFixed(0) : 0}%
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{formatCurrency(c.depreciatedValue)}</TableCell>
                        <TableCell>{c.totalUsers}</TableCell>
                        <TableCell>{c.adminUsers}</TableCell>
                        <TableCell>{c.averageAssetAge.toFixed(0)}m</TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">{formatDate(c.createdAt)}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                Showing {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, filteredData.length)} of {filteredData.length}
              </Typography>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                sx={{ border: 'none', overflow: 'hidden' }}
              />
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default SuperAdminReports;